/**
 * Administration Data Map - Pure Transformation, Search, and Traversal Helpers (ODS DATA MAP V1).
 *
 * This module contains pure, deterministic functions for data catalog transformations,
 * role authorization, multi-faceted search, relationship graph traversal, and dictionary
 * flattening. It does not import React, DOM, or database drivers, making it fully testable
 * in Node/Vitest environments.
 */

import {
  VIEW_DATA_CATALOG,
  hasAdministrationCapability,
} from "@shared/administration/capabilities";
import type {
  DataCatalogArtifact,
  DomainId,
  SemanticDomainMetadata,
  TableDefinition,
  ColumnDefinition,
  ColumnReference,
  ForeignKeyDefinition,
  SourceOfTruthClassification,
  EntityStatus,
  DataSensitivity,
  MigrationSummary,
  DataCatalogHealth,
} from "@shared/data-catalog/types";

export const DATA_MAP_ROUTE = "/administration/data-map" as const;

export const AUTHORIZED_DATA_MAP_ROLES = ["admin", "admin_manager"] as const;
export type AuthorizedDataMapRole = typeof AUTHORIZED_DATA_MAP_ROLES[number];

/**
 * Expected 13 semantic domain IDs in canonical display order.
 */
export const ALL_DOMAIN_IDS: readonly DomainId[] = [
  "entidades_clientes",
  "contactos",
  "sedes",
  "contratos",
  "instalaciones",
  "facturacion",
  "finanzas",
  "tecnicos",
  "partes",
  "materiales",
  "catalogo",
  "documentos",
  "sistema",
] as const;

export interface DomainSummaryItem {
  id: DomainId;
  humanName: string;
  description: string;
  keyConcepts: string[];
  tables: string[];
  tableCount: number;
}

export interface CatalogSummaryMetrics {
  totalDomains: number;
  totalTables: number;
  totalColumns: number;
  totalEnums: number;
  totalForeignKeys: number;
  totalIndexes: number;
  totalChecks: number;
  isHealthy: boolean;
  totalRedactedColumnsCount: number;
}

export interface TableRelationshipItem {
  sourceTable: string;
  sourceColumn: string;
  targetTable: string;
  targetColumn: string;
  direction: "outgoing" | "incoming";
  onDelete?: string;
  onUpdate?: string;
}

export interface DictionaryFieldItem {
  domainId: DomainId;
  domainHumanName: string;
  tableName: string;
  tableHumanName: string;
  columnName: string;
  columnHumanName: string;
  technicalPath: string; // e.g. "businessPartners.taxId"
  physicalType: string;
  normalizedType: string;
  isNullable: boolean;
  isPrimaryKey: boolean;
  defaultValue?: string | null;
  references: ColumnReference | null;
  referencesDisplay: string; // e.g. "businessPartners.id" or "—"
  sourceOfTruth: SourceOfTruthClassification;
  status: EntityStatus;
  sensitivity: DataSensitivity;
  synonyms: string[];
  description: string;
  notes?: string;
  businessRules?: string[];
  enumValues?: string[];
}

export interface SearchMatchDetail {
  type: "domain" | "table" | "field";
  domainId: DomainId;
  domainHumanName: string;
  tableName?: string;
  tableHumanName?: string;
  columnName?: string;
  columnHumanName?: string;
  technicalPath?: string;
  matchedProperty: string;
  matchedValue: string;
}

export interface SearchResults {
  query: string;
  totalMatches: number;
  domains: DomainSummaryItem[];
  tables: TableDefinition[];
  fields: DictionaryFieldItem[];
  details: SearchMatchDetail[];
}

export interface FormattedMigrationItem {
  file: string;
  tag: string;
  operationsCount: number;
  safeOperationsSummary: string[];
  tablesAffected: string[];
}

export interface CatalogHealthMetrics extends DataCatalogHealth {
  totalRedactedColumnsCount: number;
  redactionReasons: Record<string, number>;
}

export interface CoreLineageStep {
  stepNumber: number;
  entityName: string;
  tableName: string;
  domainId: DomainId;
  domainHumanName: string;
  tableHumanName: string;
  description: string;
  connectionField?: string;
  targetTable?: string;
  targetField?: string;
}

/**
 * Returns true if the user role is authorized to view the Data Map cockpit.
 * Fail-closed: only "admin" and "admin_manager" return true.
 */
export function canAccessDataMap(role: string | null | undefined): boolean {
  return hasAdministrationCapability(role, VIEW_DATA_CATALOG);
}

/**
 * Normalizes text for case-insensitive, accent-insensitive search comparisons.
 */
export function normalizeSearchString(val: unknown): string {
  if (typeof val !== "string") return "";
  return val
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * Returns structured domain summaries for all domains present in the catalog.
 */
export function getDomainList(catalog: DataCatalogArtifact | null | undefined): DomainSummaryItem[] {
  if (!catalog || !catalog.domains || typeof catalog.domains !== "object") {
    return [];
  }

  const result: DomainSummaryItem[] = [];

  for (const domainId of ALL_DOMAIN_IDS) {
    const domainMeta = catalog.domains[domainId];
    if (domainMeta) {
      result.push({
        id: domainMeta.id ?? domainId,
        humanName: domainMeta.humanName || domainId,
        description: domainMeta.description || "",
        keyConcepts: Array.isArray(domainMeta.keyConcepts) ? domainMeta.keyConcepts : [],
        tables: Array.isArray(domainMeta.tables) ? domainMeta.tables : [],
        tableCount: Array.isArray(domainMeta.tables) ? domainMeta.tables.length : 0,
      });
    }
  }

  // Also include any extra domains that might exist outside canonical ALL_DOMAIN_IDS
  for (const [key, val] of Object.entries(catalog.domains)) {
    if (!ALL_DOMAIN_IDS.includes(key as DomainId) && val) {
      result.push({
        id: (val.id ?? key) as DomainId,
        humanName: val.humanName || key,
        description: val.description || "",
        keyConcepts: Array.isArray(val.keyConcepts) ? val.keyConcepts : [],
        tables: Array.isArray(val.tables) ? val.tables : [],
        tableCount: Array.isArray(val.tables) ? val.tables.length : 0,
      });
    }
  }

  return result;
}

/**
 * Extracts aggregate summary metrics from the data catalog DTO.
 */
export function getCatalogSummaryMetrics(
  catalog: DataCatalogArtifact | null | undefined
): CatalogSummaryMetrics {
  const summary = catalog?.summary;
  const health = summary?.health;
  const redactions = summary?.redactions;

  return {
    totalDomains: summary?.totalDomains ?? Object.keys(catalog?.domains ?? {}).length,
    totalTables: summary?.totalTables ?? Object.keys(catalog?.tables ?? {}).length,
    totalColumns: summary?.totalColumns ?? 0,
    totalEnums: summary?.totalEnums ?? Object.keys(catalog?.enums ?? {}).length,
    totalForeignKeys: health?.foreignKeysCount ?? 0,
    totalIndexes: health?.indexesCount ?? 0,
    totalChecks: health?.checksCount ?? 0,
    isHealthy: health?.isHealthy ?? true,
    totalRedactedColumnsCount: redactions?.totalRedactedColumnsCount ?? 0,
  };
}

/**
 * Flattens all table column definitions into a flat field inventory for the Data Dictionary.
 * Strictly metadata-only: does not touch or contain any business record values.
 */
export function flattenDataDictionary(
  catalog: DataCatalogArtifact | null | undefined
): DictionaryFieldItem[] {
  if (!catalog || !catalog.tables || typeof catalog.tables !== "object") {
    return [];
  }

  const items: DictionaryFieldItem[] = [];
  const domains = catalog.domains ?? {};

  for (const [tableName, table] of Object.entries(catalog.tables)) {
    if (!table || !table.columns) continue;

    const domainMeta = domains[table.domain];
    const domainHumanName = domainMeta?.humanName || table.domain || "General";
    const tableHumanName = table.humanName || tableName;

    for (const [colName, col] of Object.entries(table.columns)) {
      if (!col) continue;

      let referencesDisplay = "—";
      if (col.references && col.references.targetTable) {
        referencesDisplay = `${col.references.targetTable}.${col.references.targetColumn || "id"}`;
      } else if (Array.isArray(table.foreignKeys)) {
        const fk = table.foreignKeys.find((f) => f.columns?.includes(colName));
        if (fk && fk.targetTable) {
          referencesDisplay = `${fk.targetTable}.${fk.targetColumns?.[0] || "id"}`;
        }
      }

      items.push({
        domainId: table.domain,
        domainHumanName,
        tableName,
        tableHumanName,
        columnName: col.name ?? colName,
        columnHumanName: col.humanName || col.name || colName,
        technicalPath: `${tableName}.${col.name ?? colName}`,
        physicalType: col.physicalType || "varchar",
        normalizedType: col.normalizedType || "string",
        isNullable: Boolean(col.isNullable),
        isPrimaryKey: Boolean(col.isPrimaryKey),
        defaultValue: col.defaultValue ?? null,
        references: col.references ?? null,
        referencesDisplay,
        sourceOfTruth: col.sourceOfTruth || table.sourceOfTruth || "canonical",
        status: col.status || table.status || "canonical",
        sensitivity: col.sensitivity || table.sensitivity || "internal",
        synonyms: Array.isArray(col.synonyms) ? col.synonyms : [],
        description: col.description || "",
        notes: col.notes,
        businessRules: Array.isArray(col.businessRules) ? col.businessRules : [],
        enumValues: Array.isArray(col.enumValues) ? col.enumValues : undefined,
      });
    }
  }

  return items;
}

/**
 * Searches the catalog across domains, tables, and columns matching:
 * - Human names / labels
 * - Technical names / paths
 * - Synonyms
 * - Descriptions / notes
 * - Key concepts
 *
 * Example: "CIF" -> resolves businessPartners.taxId
 * Example: "precio contrato" -> resolves customerContracts.originalContractAmount
 */
export function searchDataMap(
  query: string,
  catalog: DataCatalogArtifact | null | undefined
): SearchResults {
  const trimmed = query.trim();
  const normalizedQuery = normalizeSearchString(trimmed);

  if (!normalizedQuery || !catalog) {
    return {
      query: trimmed,
      totalMatches: 0,
      domains: [],
      tables: [],
      fields: [],
      details: [],
    };
  }

  const matchedDomains = new Map<DomainId, DomainSummaryItem>();
  const matchedTables = new Map<string, TableDefinition>();
  const matchedFields = new Map<string, DictionaryFieldItem>();
  const details: SearchMatchDetail[] = [];

  const domainsList = getDomainList(catalog);
  const flattenedFields = flattenDataDictionary(catalog);

  // 1. Search Domains
  for (const domain of domainsList) {
    const domainNormHuman = normalizeSearchString(domain.humanName);
    const domainNormId = normalizeSearchString(domain.id);
    const domainNormDesc = normalizeSearchString(domain.description);

    if (domainNormHuman.includes(normalizedQuery)) {
      matchedDomains.set(domain.id, domain);
      details.push({
        type: "domain",
        domainId: domain.id,
        domainHumanName: domain.humanName,
        matchedProperty: "humanName",
        matchedValue: domain.humanName,
      });
    } else if (domainNormId.includes(normalizedQuery)) {
      matchedDomains.set(domain.id, domain);
      details.push({
        type: "domain",
        domainId: domain.id,
        domainHumanName: domain.humanName,
        matchedProperty: "id",
        matchedValue: domain.id,
      });
    } else if (domainNormDesc.includes(normalizedQuery)) {
      matchedDomains.set(domain.id, domain);
      details.push({
        type: "domain",
        domainId: domain.id,
        domainHumanName: domain.humanName,
        matchedProperty: "description",
        matchedValue: domain.description,
      });
    } else {
      const matchedConcept = domain.keyConcepts.find((c) =>
        normalizeSearchString(c).includes(normalizedQuery)
      );
      if (matchedConcept) {
        matchedDomains.set(domain.id, domain);
        details.push({
          type: "domain",
          domainId: domain.id,
          domainHumanName: domain.humanName,
          matchedProperty: "keyConcepts",
          matchedValue: matchedConcept,
        });
      }
    }
  }

  // 2. Search Tables
  if (catalog.tables) {
    for (const [tableName, table] of Object.entries(catalog.tables)) {
      const tableNormHuman = normalizeSearchString(table.humanName);
      const tableNormName = normalizeSearchString(table.name);
      const tableNormDesc = normalizeSearchString(table.description);

      const domainMeta = catalog.domains?.[table.domain];
      const domainHumanName = domainMeta?.humanName || table.domain;

      let tableMatched = false;

      if (tableNormHuman.includes(normalizedQuery)) {
        matchedTables.set(tableName, table);
        details.push({
          type: "table",
          domainId: table.domain,
          domainHumanName,
          tableName,
          tableHumanName: table.humanName,
          matchedProperty: "humanName",
          matchedValue: table.humanName,
        });
        tableMatched = true;
      } else if (tableNormName.includes(normalizedQuery)) {
        matchedTables.set(tableName, table);
        details.push({
          type: "table",
          domainId: table.domain,
          domainHumanName,
          tableName,
          tableHumanName: table.humanName,
          matchedProperty: "name",
          matchedValue: table.name,
        });
        tableMatched = true;
      } else if (tableNormDesc.includes(normalizedQuery)) {
        matchedTables.set(tableName, table);
        details.push({
          type: "table",
          domainId: table.domain,
          domainHumanName,
          tableName,
          tableHumanName: table.humanName,
          matchedProperty: "description",
          matchedValue: table.description,
        });
        tableMatched = true;
      } else {
        const matchedConcept = (table.keyConcepts || []).find((c) =>
          normalizeSearchString(c).includes(normalizedQuery)
        );
        if (matchedConcept) {
          matchedTables.set(tableName, table);
          details.push({
            type: "table",
            domainId: table.domain,
            domainHumanName,
            tableName,
            tableHumanName: table.humanName,
            matchedProperty: "keyConcepts",
            matchedValue: matchedConcept,
          });
          tableMatched = true;
        }
      }
    }
  }

  // 3. Search Columns / Fields
  for (const field of flattenedFields) {
    const fieldNormHuman = normalizeSearchString(field.columnHumanName);
    const fieldNormName = normalizeSearchString(field.columnName);
    const fieldNormPath = normalizeSearchString(field.technicalPath);
    const fieldNormDesc = normalizeSearchString(field.description);
    const fieldNormNotes = normalizeSearchString(field.notes);
    const synonymsNorm = field.synonyms.map(normalizeSearchString);

    let matchedProp: string | null = null;
    let matchedVal = "";

    if (synonymsNorm.some((syn) => syn.includes(normalizedQuery))) {
      matchedProp = "synonyms";
      const exactSyn = field.synonyms.find((s) =>
        normalizeSearchString(s).includes(normalizedQuery)
      );
      matchedVal = exactSyn || field.synonyms.join(", ");
    } else if (fieldNormHuman.includes(normalizedQuery)) {
      matchedProp = "humanName";
      matchedVal = field.columnHumanName;
    } else if (fieldNormName.includes(normalizedQuery) || fieldNormPath.includes(normalizedQuery)) {
      matchedProp = "name";
      matchedVal = field.technicalPath;
    } else if (fieldNormDesc.includes(normalizedQuery)) {
      matchedProp = "description";
      matchedVal = field.description;
    } else if (fieldNormNotes.includes(normalizedQuery)) {
      matchedProp = "notes";
      matchedVal = field.notes || "";
    } else if (
      field.businessRules &&
      field.businessRules.some((br) => normalizeSearchString(br).includes(normalizedQuery))
    ) {
      matchedProp = "businessRules";
      matchedVal = field.businessRules.join("; ");
    }

    if (matchedProp) {
      matchedFields.set(field.technicalPath, field);
      details.push({
        type: "field",
        domainId: field.domainId,
        domainHumanName: field.domainHumanName,
        tableName: field.tableName,
        tableHumanName: field.tableHumanName,
        columnName: field.columnName,
        columnHumanName: field.columnHumanName,
        technicalPath: field.technicalPath,
        matchedProperty: matchedProp,
        matchedValue: matchedVal,
      });

      // Also ensure parent table is indexed if field matched
      if (catalog.tables?.[field.tableName] && !matchedTables.has(field.tableName)) {
        matchedTables.set(field.tableName, catalog.tables[field.tableName]);
      }
    }
  }

  const totalMatches = matchedDomains.size + matchedTables.size + matchedFields.size;

  return {
    query: trimmed,
    totalMatches,
    domains: Array.from(matchedDomains.values()),
    tables: Array.from(matchedTables.values()),
    fields: Array.from(matchedFields.values()),
    details,
  };
}

/**
 * Discovers and extracts outgoing and incoming foreign key relationships for a given table.
 */
export function getTableRelationships(
  tableName: string,
  catalog: DataCatalogArtifact | null | undefined
): {
  outgoing: TableRelationshipItem[];
  incoming: TableRelationshipItem[];
} {
  const outgoing: TableRelationshipItem[] = [];
  const incoming: TableRelationshipItem[] = [];

  if (!catalog || !catalog.tables || typeof catalog.tables !== "object") {
    return { outgoing, incoming };
  }

  const currentTable = catalog.tables[tableName];
  if (!currentTable) {
    return { outgoing, incoming };
  }

  // 1. Outgoing from currentTable columns and foreignKeys
  if (currentTable.columns) {
    for (const [colName, col] of Object.entries(currentTable.columns)) {
      if (col?.references && col.references.targetTable) {
        outgoing.push({
          sourceTable: tableName,
          sourceColumn: colName,
          targetTable: col.references.targetTable,
          targetColumn: col.references.targetColumn || "id",
          direction: "outgoing",
          onDelete: col.references.onDelete,
          onUpdate: col.references.onUpdate,
        });
      }
    }
  }

  if (Array.isArray(currentTable.foreignKeys)) {
    for (const fk of currentTable.foreignKeys) {
      if (fk && fk.targetTable) {
        const sourceCol = fk.columns?.[0] || "";
        const targetCol = fk.targetColumns?.[0] || "id";
        const exists = outgoing.some(
          (o) =>
            o.sourceColumn === sourceCol &&
            o.targetTable === fk.targetTable &&
            o.targetColumn === targetCol
        );
        if (!exists) {
          outgoing.push({
            sourceTable: tableName,
            sourceColumn: sourceCol,
            targetTable: fk.targetTable,
            targetColumn: targetCol,
            direction: "outgoing",
            onDelete: fk.onDelete,
          });
        }
      }
    }
  }

  // 2. Incoming from all other tables pointing to currentTable
  for (const [otherTableName, otherTable] of Object.entries(catalog.tables)) {
    if (otherTableName === tableName || !otherTable) continue;

    if (otherTable.columns) {
      for (const [colName, col] of Object.entries(otherTable.columns)) {
        if (col?.references && col.references.targetTable === tableName) {
          incoming.push({
            sourceTable: otherTableName,
            sourceColumn: colName,
            targetTable: tableName,
            targetColumn: col.references.targetColumn || "id",
            direction: "incoming",
            onDelete: col.references.onDelete,
            onUpdate: col.references.onUpdate,
          });
        }
      }
    }

    if (Array.isArray(otherTable.foreignKeys)) {
      for (const fk of otherTable.foreignKeys) {
        if (fk && fk.targetTable === tableName) {
          const sourceCol = fk.columns?.[0] || "";
          const targetCol = fk.targetColumns?.[0] || "id";
          const exists = incoming.some(
            (i) =>
              i.sourceTable === otherTableName &&
              i.sourceColumn === sourceCol &&
              i.targetColumn === targetCol
          );
          if (!exists) {
            incoming.push({
              sourceTable: otherTableName,
              sourceColumn: sourceCol,
              targetTable: tableName,
              targetColumn: targetCol,
              direction: "incoming",
              onDelete: fk.onDelete,
            });
          }
        }
      }
    }
  }

  return { outgoing, incoming };
}

/**
 * Returns the canonical operational relationship chain:
 * Entidad/Cliente (businessPartners) -> Contrato (customerContracts) -> Instalación (installations).
 */
export function getCoreRelationshipChain(
  catalog: DataCatalogArtifact | null | undefined
): {
  chain: CoreLineageStep[];
  isValid: boolean;
} {
  const chain: CoreLineageStep[] = [
    {
      stepNumber: 1,
      entityName: "Entidad / Cliente (Customer 360)",
      tableName: "businessPartners",
      domainId: "entidades_clientes",
      domainHumanName: "Entidades y Clientes",
      tableHumanName: "Entidades y Terceros de Negocio",
      description: "Identidad canónica unificada de cliente, CIF/NIF y estructura societaria.",
    },
    {
      stepNumber: 2,
      entityName: "Contrato Comercial",
      tableName: "customerContracts",
      domainId: "contratos",
      domainHumanName: "Contratos Comerciales",
      tableHumanName: "Contratos Comerciales de Clientes",
      description: "Acuerdo comercial formal, alcance económico y estrategia de facturación.",
      connectionField: "customerPartnerId",
      targetTable: "businessPartners",
      targetField: "id",
    },
    {
      stepNumber: 3,
      entityName: "Instalación Fotovoltaica",
      tableName: "installations",
      domainId: "instalaciones",
      domainHumanName: "Instalaciones y Proyectos FV",
      tableHumanName: "Instalaciones Fotovoltaicas",
      description: "Obra física técnica, ejecución en campo y control de materiales/partes.",
      connectionField: "contractId",
      targetTable: "customerContracts",
      targetField: "id",
    },
  ];

  if (!catalog || !catalog.tables) {
    return { chain, isValid: false };
  }

  const contracts = catalog.tables.customerContracts;
  const installations = catalog.tables.installations;

  const contractToPartner =
    contracts?.columns?.customerPartnerId?.references?.targetTable === "businessPartners" ||
    contracts?.foreignKeys?.some((fk) => fk.targetTable === "businessPartners");

  const installToContract =
    installations?.columns?.contractId?.references?.targetTable === "customerContracts" ||
    installations?.foreignKeys?.some((fk) => fk.targetTable === "customerContracts");

  const isValid = Boolean(contractToPartner && installToContract);

  return { chain, isValid };
}

/**
 * Returns formatted migration history items from catalog migrations metadata.
 * Strips raw SQL bodies and provides structured governance summaries.
 */
export function getMigrationSummaries(
  catalog: DataCatalogArtifact | null | undefined
): FormattedMigrationItem[] {
  if (!catalog || !Array.isArray(catalog.migrations)) {
    return [];
  }

  return catalog.migrations.map((m: MigrationSummary) => ({
    file: m.file || "unknown.sql",
    tag: m.tag || m.file || "migration",
    operationsCount: Array.isArray(m.safeOperationsSummary) ? m.safeOperationsSummary.length : 0,
    safeOperationsSummary: Array.isArray(m.safeOperationsSummary) ? m.safeOperationsSummary : [],
    tablesAffected: Array.isArray(m.tablesAffected) ? m.tablesAffected : [],
  }));
}

/**
 * Extracts aggregate catalog health and quality metrics.
 */
export function getCatalogHealthMetrics(
  catalog: DataCatalogArtifact | null | undefined
): CatalogHealthMetrics {
  const health = catalog?.summary?.health;
  const redactions = catalog?.summary?.redactions;

  return {
    totalDomainsCount: health?.totalDomainsCount ?? 13,
    assignedTablesCount: health?.assignedTablesCount ?? 0,
    unassignedTablesCount: health?.unassignedTablesCount ?? 0,
    missingDomainTables: Array.isArray(health?.missingDomainTables)
      ? health.missingDomainTables
      : [],
    canonicalTablesCount: health?.canonicalTablesCount ?? 0,
    compatibilityTablesCount: health?.compatibilityTablesCount ?? 0,
    legacyTablesCount: health?.legacyTablesCount ?? 0,
    derivedTablesCount: health?.derivedTablesCount ?? 0,
    reviewTablesCount: health?.reviewTablesCount ?? 0,
    deprecatedTablesCount: health?.deprecatedTablesCount ?? 0,
    foreignKeysCount: health?.foreignKeysCount ?? 0,
    indexesCount: health?.indexesCount ?? 0,
    checksCount: health?.checksCount ?? 0,
    isHealthy: health?.isHealthy ?? true,
    totalRedactedColumnsCount: redactions?.totalRedactedColumnsCount ?? 0,
    redactionReasons: redactions?.redactionReasons ?? {},
  };
}

/**
 * Badge representation for Source of Truth classifications.
 */
export function getSourceOfTruthBadge(sot: SourceOfTruthClassification | undefined): {
  label: string;
  className: string;
} {
  switch (sot) {
    case "canonical":
      return {
        label: "Canónica",
        className: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800",
      };
    case "compatibility":
      return {
        label: "Compatibilidad",
        className: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800",
      };
    case "legacy":
      return {
        label: "Legacy",
        className: "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800",
      };
    case "derived":
      return {
        label: "Derivada",
        className: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800",
      };
    case "review":
      return {
        label: "En Revisión",
        className: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800",
      };
    case "conflict":
      return {
        label: "Conflicto",
        className: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800",
      };
    default:
      return {
        label: sot || "Canónica",
        className: "bg-zinc-100 text-zinc-700 border-zinc-200",
      };
  }
}

/**
 * Badge representation for Data Sensitivity levels.
 */
export function getSensitivityBadge(sens: DataSensitivity | undefined): {
  label: string;
  className: string;
} {
  switch (sens) {
    case "public":
      return {
        label: "Pública",
        className: "bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300",
      };
    case "internal":
      return {
        label: "Interna",
        className: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800",
      };
    case "confidential":
      return {
        label: "Confidencial",
        className: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800",
      };
    case "restricted":
      return {
        label: "Restringida",
        className: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800",
      };
    case "pii":
      return {
        label: "PII (Personal)",
        className: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800",
      };
    default:
      return {
        label: sens || "Interna",
        className: "bg-zinc-100 text-zinc-700 border-zinc-200",
      };
  }
}

/**
 * Badge representation for Entity lifecycle status.
 */
export function getEntityStatusBadge(status: EntityStatus | undefined): {
  label: string;
  className: string;
} {
  switch (status) {
    case "canonical":
      return {
        label: "Canónico",
        className: "bg-emerald-50 text-emerald-700 border-emerald-200",
      };
    case "compatibility":
      return {
        label: "Compatibilidad",
        className: "bg-amber-50 text-amber-700 border-amber-200",
      };
    case "legacy":
      return {
        label: "Legacy",
        className: "bg-orange-50 text-orange-700 border-orange-200",
      };
    case "derived":
      return {
        label: "Derivado",
        className: "bg-sky-50 text-sky-700 border-sky-200",
      };
    case "review":
      return {
        label: "En Revisión",
        className: "bg-purple-50 text-purple-700 border-purple-200",
      };
    case "deprecated":
      return {
        label: "Deprecado",
        className: "bg-zinc-100 text-zinc-600 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-400",
      };
    default:
      return {
        label: status || "Canónico",
        className: "bg-zinc-100 text-zinc-700 border-zinc-200",
      };
  }
}
